import { useState, useCallback, useEffect, useRef } from 'react';
import PT from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import cx from 'classnames';
import { Loader } from 'edrolo-ui';
import style from './PdfViewer.scss';

// Configure the PDF.js worker. We import the minified worker file which
// webpack emits as a static asset via the asset/resource rule.
// eslint-disable-next-line import/no-unresolved, import/extensions
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PdfViewer = ({ url, style: containerStyle }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [pageInputValue, setPageInputValue] = useState('1');
  const containerRef = useRef(null);

  // Reset state when url changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setNumPages(null);
    setPageNumber(1);
    setPageInputValue('1');
  }, [url]);

  // Measure container width for responsive scaling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateWidth = () => {
      const nextWidth = container.clientWidth;
      setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Keyboard navigation scoped to the viewer container
  const handleKeyDown = useCallback(
    (e) => {
      // Ignore key events from form controls
      if (
        e.target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setPageNumber((prev) => Math.max(1, prev - 1));
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setPageNumber((prev) =>
          numPages ? Math.min(numPages, prev + 1) : prev
        );
      }
    },
    [numPages]
  );

  // Sync page input value when page number changes (via buttons or keyboard)
  useEffect(() => {
    setPageInputValue(String(pageNumber));
  }, [pageNumber]);

  const handleDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setPageNumber((prev) => Math.min(prev, total));
    setLoading(false);
    setError(null);
  }, []);

  const handleDocumentLoadError = useCallback((err) => {
    setLoading(false);
    setError(err?.message || 'Failed to load PDF document.');
  }, []);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => (numPages ? Math.min(numPages, prev + 1) : prev));
  }, [numPages]);

  const handlePageInputChange = useCallback((e) => {
    setPageInputValue(e.target.value);
  }, []);

  const handlePageInputBlur = useCallback(() => {
    const parsed = parseInt(pageInputValue, 10);
    if (!isNaN(parsed) && parsed >= 1 && numPages && parsed <= numPages) {
      setPageNumber(parsed);
    } else {
      setPageInputValue(String(pageNumber));
    }
  }, [pageInputValue, numPages, pageNumber]);

  const handlePageInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  if (error) {
    return (
      <div
        className={style.pdfViewer}
        style={containerStyle}
        data-testid="pdf-viewer-error"
      >
        <div className={style.errorContainer} role="alert">
          <p className={style.errorMessage}>
            Unable to load PDF document. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={style.pdfViewer}
      style={containerStyle}
      ref={containerRef}
      data-testid="pdf-viewer"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {loading && (
        <div
          className={style.loadingContainer}
          data-testid="pdf-viewer-loading"
          role="status"
          aria-live="polite"
        >
          <Loader.Spinner />
          <p className={style.loadingText}>Loading PDF...</p>
        </div>
      )}

      <div className={cx(style.documentContainer, { [style.hidden]: loading })}>
        <Document
          file={url}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          loading={null}
        >
          <Page
            pageNumber={pageNumber}
            width={containerWidth || undefined}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>

      {numPages && (
        <div className={style.navigation} data-testid="pdf-viewer-navigation">
          <button
            type="button"
            className={style.navButton}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            aria-label="Previous page"
            data-testid="pdf-viewer-prev"
          >
            &lsaquo; Prev
          </button>

          <span className={style.pageIndicator}>
            Page{' '}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={style.pageInput}
              value={pageInputValue}
              onChange={handlePageInputChange}
              onBlur={handlePageInputBlur}
              onKeyDown={handlePageInputKeyDown}
              aria-label="Page number"
              data-testid="pdf-viewer-page-input"
            />{' '}
            of {numPages}
          </span>

          <button
            type="button"
            className={style.navButton}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            aria-label="Next page"
            data-testid="pdf-viewer-next"
          >
            Next &rsaquo;
          </button>
        </div>
      )}
    </div>
  );
};

PdfViewer.propTypes = {
  url: PT.string.isRequired,
  style: PT.object,
};

export default PdfViewer;
